import { useEffect, useRef, useState } from "react";

interface UseAnimatedValueOptions {
  duration?: number;
}

export function useAnimatedValue(
  target: number,
  options?: UseAnimatedValueOptions,
): number {
  const duration = options?.duration ?? 500;
  const [value, setValue] = useState(target);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);
  const fromRef = useRef(target);
  const reducedMotion = useRef(false);

  useEffect(() => {
    reducedMotion.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
  }, []);

  useEffect(() => {
    if (reducedMotion.current) {
      setValue(target);
      return;
    }

    const from = fromRef.current;
    if (from === target) return;

    cancelAnimationFrame(rafRef.current);
    startRef.current = 0;

    const animate = (timestamp: number) => {
      if (!startRef.current) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = from + (target - from) * eased;

      setValue(current);
      fromRef.current = current;

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  return value;
}
